import store from "@/store";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/zh";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);

export const fileSizeFormat = (bytes: any): string => {
  if (isNaN(Number(bytes))) return bytes.toString();
  if (bytes === 0) return "0B";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(Number(bytes)) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + sizes[i];
};

export const getLocale = () => {
  const lang = store.state.LanguagePicker.languageSelected;
  switch (lang) {
    case "eng":
      return "en";
    case "eng_us":
      return "en-us";
    case "chi":
      return "zh";
    default:
      return "fr";
  }
};

export const convertUTCTimeToLocalTime = (
  utcTime: string,
  locale = getLocale(),
  timeFormat = "",
  monthFormat?,
  distanceChar?,
): string => {
  if (utcTime === null || utcTime === undefined) {
    const errorMessage =
      "Invalid date input: Input cannot be null or undefined.";
    throw new Error(errorMessage);
  }
  const dateByLocale = dayjs(utcTime).locale(locale);
  let formattedDate = "";
  switch (locale) {
    case "en":
      formattedDate = dateByLocale.format(
        `D ${monthFormat ? monthFormat : "MMM"} YYYY${
          distanceChar ? distanceChar : " "
        }${timeFormat ? `${timeFormat}` : ""}`,
      );
      break;
    case "en-us":
      formattedDate = dateByLocale.format(
        `${monthFormat ? monthFormat : "MMM"} D, YYYY${
          distanceChar ? distanceChar : " "
        }${timeFormat ? `${timeFormat}` : ""}`,
      );
      break;
    case "fr":
      formattedDate = dateByLocale.format(
        `D ${monthFormat ? monthFormat : "MMM"} YYYY${
          distanceChar ? distanceChar : " "
        }${timeFormat ? `${timeFormat}` : ""}`,
      );
      break;
    case "zh":
      formattedDate = dateByLocale.format(
        `YYYY-${monthFormat ? monthFormat : "MMM"}-DD${
          distanceChar ? distanceChar : " "
        }${timeFormat ? `${timeFormat}` : ""}`,
      );
      break;
    default:
      formattedDate = dateByLocale.format(
        `D ${monthFormat ? monthFormat : "MMM"} YYYY${
          distanceChar ? distanceChar : " "
        }${timeFormat ? `${timeFormat}` : ""}`,
      );
      break;
  }
  return formattedDate;
};

export const convertUTCTimeToLocalTimeAndFormat = (payload: {
  utcTime: string;
  locale?: string;
  customParseFormat: string;
}): Dayjs => {
  const { utcTime, locale = getLocale(), customParseFormat } = payload;
  return dayjs(utcTime, customParseFormat).locale(locale);
};

export const getTimeFromNow = (
  utcTime: string,
  locale = getLocale(),
): string => {
  return dayjs(utcTime).locale(locale).fromNow();
};
